import React, { useState } from 'react';
import { BookOpen, Compass, Sparkles, Lightbulb, Target, ChevronDown, FileText } from 'lucide-react';
import NexusFAQs from './NexusFAQs';
import { CollapsibleSection, SectionGroup } from './CollapsibleSection';

/**
 * ProcessIntro
 * Static, clinician-facing overview of the four-part Phase 5 output:
 * SQIS → WHY → NEXT → NEXUS. Rendered as the first tab in the Phase 5
 * tab strip so therapists always have context for what each subsequent
 * section is meant to deliver.
 */
const STEPS = [
  {
    key: 'sqis',
    number: '1',
    title: 'SQIS',
    tagline: 'The Raw Narrative',
    accent: 'indigo',
    icon: BookOpen,
    what: "The 14-point structured summary of the client's intake, answers, and background.",
    simple: "This is the Raw Data. It captures the facts, the context, and the client's initial story without filtering or interpreting it.",
  },
  {
    key: 'why',
    number: '2',
    title: 'WHY',
    tagline: 'The Deep Conceptualization',
    accent: 'blue',
    icon: Compass,
    what: "The AI reads the SQIS and connects the dots to find hidden patterns, paradoxes, cultural tensions, and core psychological struggles.",
    simple: "This is the Sensemaking. It tells the therapist why the client feels stuck by exploring the underlying dynamics and systemic pressures beneath the surface, without pathologizing or diagnosing them.",
  },
  {
    key: 'next',
    number: '3',
    title: 'NEXT',
    tagline: 'The Action Catalyst',
    accent: 'amber',
    icon: Lightbulb,
    what: "The AI takes the \"Why\" conceptualization and designs small, safe, culturally-tailored \"micro-experiments\" for the client to try in the real world.",
    simple: "This is the Exploration Plan. It gives the therapist and client a concrete, low-stakes way to test new behaviors and shift the hidden dynamics identified in the Why Analysis.",
  },
  {
    key: 'nexus',
    number: '4',
    title: 'NEXUS',
    tagline: 'The Friction Simulator',
    accent: 'emerald',
    icon: Target,
    what: "The AI turns evidence-linked findings into possible response scenarios, resistance guidance, and cultural or systemic questions. It lets the therapist rehearse difficult conversations before the real session without predicting what the client will say.",
    simple: "This is the Guided Conversation and Rehearsal. It connects evidence to objection handling, motivational interviewing responses, and optional live simulation.",
  },
];

const ACCENT_MAP = {
  indigo: {
    ring: 'border-indigo-200',
    bg: 'bg-indigo-50/40',
    numberBg: 'bg-indigo-600',
    numberText: 'text-white',
    text: 'text-indigo-900',
    tagText: 'text-indigo-700',
    iconBg: 'bg-indigo-100',
    iconText: 'text-indigo-700',
  },
  blue: {
    ring: 'border-blue-200',
    bg: 'bg-blue-50/40',
    numberBg: 'bg-blue-600',
    numberText: 'text-white',
    text: 'text-blue-900',
    tagText: 'text-blue-700',
    iconBg: 'bg-blue-100',
    iconText: 'text-blue-700',
  },
  amber: {
    ring: 'border-amber-200',
    bg: 'bg-amber-50/40',
    numberBg: 'bg-amber-600',
    numberText: 'text-white',
    text: 'text-amber-900',
    tagText: 'text-amber-700',
    iconBg: 'bg-amber-100',
    iconText: 'text-amber-700',
  },
  emerald: {
    ring: 'border-emerald-200',
    bg: 'bg-emerald-50/40',
    numberBg: 'bg-emerald-600',
    numberText: 'text-white',
    text: 'text-emerald-900',
    tagText: 'text-emerald-700',
    iconBg: 'bg-emerald-100',
    iconText: 'text-emerald-700',
  },
  purple: {
    ring: 'border-purple-200', bg: 'bg-purple-50/40', numberBg: 'bg-purple-600', numberText: 'text-white',
    text: 'text-purple-900', tagText: 'text-purple-700', iconBg: 'bg-purple-100', iconText: 'text-purple-700',
  },
};

const ProcessIntro = ({ synthesisPoints = [] }) => {
  const [showNexusFaqs, setShowNexusFaqs] = useState(false);
  const [showRefsMethodology, setShowRefsMethodology] = useState(false);
  const p15 = synthesisPoints.find((p) => p.point_number === 15);
  const p16 = synthesisPoints.find((p) => p.point_number === 16);
  const hasRefsOrMethod = Boolean(p15 || p16);
  return (
    <div className="p-4" data-testid="process-intro-section">
      <div className="flex items-center gap-2 mb-1">
        <Sparkles className="w-5 h-5 text-indigo-700" />
        <h3 className="text-lg font-semibold text-gray-900" data-testid="process-intro-title">
          Process Intro
        </h3>
      </div>
      <p className="text-sm text-gray-600 mb-6 italic">
        A quick map of the four AI-assisted sections you&apos;ll see in this phase, and what each is
        meant to give you before you walk into session.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {STEPS.map((s) => {
          const a = ACCENT_MAP[s.accent];
          const Icon = s.icon;
          const isNexus = s.key === 'nexus';
          const isSqis = s.key === 'sqis';
          const expandFull = (isNexus && showNexusFaqs) || (isSqis && showRefsMethodology);
          return (
            <div
              key={s.key}
              className={`border ${a.ring} ${a.bg} rounded-xl p-4 flex gap-3 ${expandFull ? 'md:col-span-2' : ''}`}
              data-testid={`process-intro-step-${s.key}`}
            >
              <div className="flex flex-col items-center gap-2 flex-shrink-0">
                <span className={`inline-flex items-center justify-center w-9 h-9 rounded-full ${a.numberBg} ${a.numberText} font-semibold`}>
                  {s.number}
                </span>
                <span className={`inline-flex items-center justify-center w-9 h-9 rounded-full ${a.iconBg}`}>
                  <Icon className={`w-4 h-4 ${a.iconText}`} />
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2 flex-wrap">
                  <h4 className={`text-base font-semibold ${a.text}`}>{s.title}</h4>
                  <span className={`text-xs font-medium ${a.tagText}`}>({s.tagline})</span>
                </div>
                <p className="text-sm text-gray-800 mt-2">
                  <span className="font-semibold">What it is:</span> {s.what}
                </p>
                <p className="text-sm text-gray-700 mt-2">
                  <span className="font-semibold">Simply put:</span> {s.simple}
                </p>
                {isSqis && (
                  <div className="mt-3 pt-3 border-t border-indigo-200">
                    <button
                      type="button"
                      onClick={() => setShowRefsMethodology((v) => !v)}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold text-indigo-800 bg-indigo-100 hover:bg-indigo-200 rounded-full px-3 py-1 transition-colors"
                      data-testid="btn-toggle-refs-methodology"
                      aria-expanded={showRefsMethodology}
                    >
                      <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showRefsMethodology ? 'rotate-180' : ''}`} />
                      {showRefsMethodology ? 'Hide References & Methodology' : 'Show References & Methodology'}
                    </button>
                    {showRefsMethodology && (
                      <div className="mt-3" data-testid="refs-methodology-inline">
                        {hasRefsOrMethod ? (
                          <SectionGroup testId="refs-methodology-group">
                            {p15 && (
                              <CollapsibleSection
                                index={15}
                                icon={BookOpen}
                                accent="sky"
                                title={p15.title || 'References'}
                                subtitle="Point 15 — APA Citations"
                                defaultOpen
                                testId="point-15-references"
                              >
                                <div className="prose prose-sm max-w-none whitespace-pre-line text-gray-800 leading-relaxed">
                                  {p15.content || 'No references available.'}
                                </div>
                              </CollapsibleSection>
                            )}
                            {p16 && (
                              <CollapsibleSection
                                index={16}
                                icon={FileText}
                                accent="slate"
                                title={p16.title || 'Methodology'}
                                subtitle="Point 16 — How the SQIS was produced"
                                testId="point-16-methodology"
                              >
                                <div className="prose prose-sm max-w-none whitespace-pre-line text-gray-800 leading-relaxed">
                                  {p16.content || 'No methodology description available.'}
                                </div>
                              </CollapsibleSection>
                            )}
                          </SectionGroup>
                        ) : (
                          <p className="text-sm text-gray-500 italic" data-testid="refs-methodology-empty">
                            References (Point 15) and Methodology (Point 16) will appear here once the 14-Point SQIS is generated.
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}
                {isNexus && (
                  <div className="mt-3 pt-3 border-t border-emerald-200">
                    <button
                      type="button"
                      onClick={() => setShowNexusFaqs((v) => !v)}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-800 bg-emerald-100 hover:bg-emerald-200 rounded-full px-3 py-1 transition-colors"
                      data-testid="btn-toggle-nexus-faqs"
                      aria-expanded={showNexusFaqs}
                    >
                      <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showNexusFaqs ? 'rotate-180' : ''}`} />
                      {showNexusFaqs ? 'Hide Nexus FAQs' : 'Show Nexus FAQs (15)'}
                    </button>
                    {showNexusFaqs && (
                      <div className="mt-3" data-testid="nexus-faqs-inline">
                        <NexusFAQs />
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4" data-testid="process-intro-additional-features">
        <div className="border border-indigo-200 bg-indigo-50/40 rounded-xl p-4 flex gap-3">
          <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-indigo-100 flex-shrink-0">
            <FileText className="w-4 h-4 text-indigo-700" />
          </span>
          <div className="flex-1 min-w-0">
            <h4 className="text-base font-semibold text-indigo-900">Client Synthesis</h4>
            <p className="text-sm text-gray-800 mt-2">
              Client Synthesis brings together the key information from the reviewed INSIGHT findings into an integrated, client-specific synthesis. It connects important patterns, contributing themes, strengths, concerns, contextual factors, and suggested areas for further exploration to provide the psychologist/domain expert with a coherent overall understanding of the client. The synthesis is intended to support professional reflection and dialogue and should not be treated as a diagnosis or stand-alone psychological assessment.
            </p>
          </div>
        </div>
        <div className="border border-purple-200 bg-purple-50/40 rounded-xl p-4 flex gap-3">
          <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-purple-100 flex-shrink-0">
            <Target className="w-4 h-4 text-purple-700" />
          </span>
          <div className="flex-1 min-w-0">
            <h4 className="text-base font-semibold text-purple-900">Custom Analysis</h4>
            <p className="text-sm text-gray-800 mt-2">
              Custom Analysis allows the psychologist/domain expert to perform a focused, case-specific analysis of the reviewed INSIGHT findings according to a selected question, topic, theme, or professional purpose. The expert provides an Analysis Name and an Expert Request/Purpose, and INSIGHT examines the available reviewed material specifically in relation to that request. This allows further exploration of areas relevant to the individual case without changing the underlying INSIGHT findings. Custom Analysis is an expert-directed exploratory function and should not be used to generate diagnoses or unsupported conclusions.
            </p>
          </div>
        </div>
      </div>

      <div className="mt-6 border-l-4 border-indigo-500 bg-indigo-50/60 rounded-r-lg p-4" data-testid="process-intro-difference">
        <h4 className="text-base font-semibold text-indigo-900">Understanding the Difference</h4>
        <p className="text-sm text-gray-800 mt-2">
          Client Synthesis provides an integrated understanding of the overall reviewed INSIGHT material.
        </p>
        <p className="text-sm text-gray-800 mt-2">
          Custom Analysis allows the psychologist/domain expert to examine the reviewed INSIGHT material in relation to a specific question, topic, theme, or professional purpose.
        </p>
        <p className="text-sm text-gray-800 mt-2 font-semibold">Conceptually:</p>
        <p className="text-sm text-gray-700 mt-2">
          <span className="font-semibold">Client Synthesis:</span><br />
          &ldquo;What does the overall INSIGHT material show when brought together?&rdquo;
        </p>
        <p className="text-sm text-gray-700 mt-2">
          <span className="font-semibold">Custom Analysis:</span><br />
          &ldquo;What does the reviewed INSIGHT material show about this particular area I want to explore?&rdquo;
        </p>
      </div>

      <div
        className="mt-6 border-l-4 border-indigo-500 bg-indigo-50/60 rounded-r-lg p-4"
        data-testid="process-intro-big-picture"
      >
        <div className="text-xs uppercase tracking-wider font-semibold text-indigo-700 mb-1">
          The Big Picture in One Sentence
        </div>
        <p className="text-sm text-gray-900 italic leading-relaxed">
          &ldquo;The SQIS captures what the client said, the WHY explores possible patterns and contributing themes, the NEXT identifies possible directions, and the NEXUS brings those insights together for psychologist-led exploration.&rdquo;
        </p>
      </div>
    </div>
  );
};

export default ProcessIntro;
